import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { VideoContent } from '../../../types/content';
import { ErrorBoundary, LoadingSpinner } from '../../common';
import { ErrorDisplay } from '../../common/ErrorBoundary';
import MediaSlider from '../MediaSlider';
import VideoThumbnail from '../../video/VideoThumbnail';
import ShortsThumbnail from '../../shorts/ShortsThumbnail';

export interface MediaSectionProps {
  title?: string;
  items: VideoContent[];
  type: 'shorts' | 'video';
  loading?: boolean;
  error?: string | null;
  hideTitle?: boolean;
  actionLabel?: string;
  className?: string;
}

export interface MediaItem {
  id: string;
  title: string;
  thumbnail: string;
  duration: number;
  createdAt: string;
  broadcastStatus?: 'upcoming' | 'live' | 'completed' | 'none';
}

const sliderClasses = {
  title: 'px-2 sm:px-6',
  content: ''
} as const;

export const MediaSection: React.FC<MediaSectionProps> = ({
  title = '',
  items = [],
  type = 'video',
  loading = false,
  error = null,
  hideTitle = false,
  actionLabel,
  className = ''
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSeeAllClick = React.useCallback(() => {
    // Create search params based on the media type
    const searchParams = new URLSearchParams();
    
    // Set the content type parameter
    searchParams.set('contentType', type);
    
    // For shorts, we might want to add a duration filter
    if (type === 'shorts') {
      searchParams.set('duration', '0-5'); // Assuming shorts are 0-5 minutes
    }
    
    // Navigate to the search page with the appropriate query parameters
    navigate(`/search?${searchParams.toString()}`);
  }, [navigate, type]);

  const renderItem = React.useCallback((item: MediaItem) => (
    type === 'shorts' ? <ShortsThumbnail {...item} /> : <VideoThumbnail {...item} />
  ), [type]);

  const mediaItems = React.useMemo(() => items.map(item => ({
    id: item.id,
    title: item.title,
    thumbnail: item.thumbnail || '',
    duration: item.duration,
    createdAt: item.createdAt,
    broadcastStatus: item.broadcastStatus
  })), [items]);

  if (loading) {
    return (
      <div className="px-2 sm:px-6">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-2 sm:px-6">
        <ErrorDisplay
          error={error}
          onRetry={() => window.location.reload()}
          compact
        />
      </div>
    );
  }

  if (!items?.length) {
    return (
      <p className="px-2 sm:px-6 text-center text-text-secondary">
        {t('common.noResults')}
      </p>
    );
  }

  return (
    <section className={className}>
      <ErrorBoundary>
        <MediaSlider
          type={type}
          title={hideTitle ? undefined : title}
          onSeeAllClick={handleSeeAllClick}
          items={mediaItems}
          renderItem={renderItem}
          titleClassName={sliderClasses.title}
          contentClassName={sliderClasses.content}
        />
      </ErrorBoundary>
    </section>
  );
}; 